import React, { useState, useEffect, useCallback } from 'react';
import { Header } from '../components/Header';
import { useAuth } from '../contexts/AuthContext';
import { InactiveSubscriptionCard } from '../components/InactiveSubscriptionCard';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Alert, AlertDescription, AlertTitle } from '../components/ui/alert';
import { BarChart, Bar, LineChart, Line, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { FiDollarSign, FiCheck, FiTarget, FiTrendingUp, FiAlertCircle, FiFilter } from 'react-icons/fi';
import { toast } from 'sonner';
import { formatBRL } from '../utils/format';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const COLORS = ['#1E3A8A', '#3B82F6', '#60A5FA', '#93C5FD'];
const CATEGORY_COLORS = ['#1E3A8A', '#3B82F6', '#60A5FA', '#93C5FD', '#DC2626', '#F59E0B', '#16A34A', '#9333EA', '#0891B2', '#DB2777', '#65A30D', '#EA580C'];

const getMonthName = (month) => {
  const months = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 
                  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];
  return months[month - 1];
};

export const Dashboard = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [breakdowns, setBreakdowns] = useState({ by_payment_method: [], by_bank: [], by_category: [] });
  const [loading, setLoading] = useState(true);

  const ADMIN_EMAILS = ['matheusrweber@gmail.com', 'keylenaiara13@gmail.com'];
  const isAdmin = Boolean(user?.email && ADMIN_EMAILS.includes(user.email));
  const isSubscriptionActive = isAdmin || user?.subscription_status === 'active' || (user?.trial_ends_at && new Date(user.trial_ends_at) > new Date());
  
  // Filtros - Default to current year with Jan-Dec
  const currentYear = new Date().getFullYear();
  const [filterYear, setFilterYear] = useState(String(currentYear));
  const [filterStartMonth, setFilterStartMonth] = useState('1');
  const [filterEndMonth, setFilterEndMonth] = useState('12');

  const buildParams = useCallback(() => {
    const params = new URLSearchParams();
    if (filterYear) params.append('year', filterYear);
    if (filterStartMonth) params.append('start_month', filterStartMonth);
    if (filterEndMonth) params.append('end_month', filterEndMonth);
    return params;
  }, [filterYear, filterStartMonth, filterEndMonth]);

  const loadStats = useCallback(async () => {
    try {
      const params = buildParams();
      const url = `${API}/dashboard/stats${params.toString() ? `?${params.toString()}` : ''}`;
      const response = await axios.get(url, { withCredentials: true });
      setStats(response.data);
    } catch (error) {
      console.error('Error loading stats:', error);
      toast.error('Erro ao carregar estatísticas');
    } finally {
      setLoading(false);
    }
  }, [buildParams]);

  const loadBreakdowns = useCallback(async () => {
    try {
      const params = buildParams();
      const url = `${API}/dashboard/breakdowns${params.toString() ? `?${params.toString()}` : ''}`;
      const response = await axios.get(url, { withCredentials: true });
      setBreakdowns(response.data || { by_payment_method: [], by_bank: [], by_category: [] });
    } catch (error) {
      console.error('Error loading breakdowns:', error);
    }
  }, [buildParams]);

  useEffect(() => {
    if (isSubscriptionActive) {
      loadStats();
      loadBreakdowns();
    } else {
      setLoading(false);
    }
  }, [isSubscriptionActive, loadStats, loadBreakdowns]);

  const handleExportPDF = () => {}; // Removed - PDF export only in Registros page

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F0F5FA] dark:bg-[#070D1A] transition-colors">
        <Header />
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-900 mx-auto"></div>
            <p className="mt-4 text-blue-900 font-medium">Carregando...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!isSubscriptionActive) {
    return (
      <div className="min-h-screen bg-[#F0F5FA] dark:bg-[#070D1A] transition-colors">
        <Header />
        <main className="transition-all duration-300">
          <InactiveSubscriptionCard moduleName="Dashboard" />
        </main>
      </div>
    );
  }

  const saldoValue = stats?.total_amount || 0;
  const paymentData = [
    { name: 'Receitas', value: stats?.receitas || 0, color: '#1E3A8A', displayValue: stats?.receitas || 0 },
    { name: 'Despesas', value: stats?.despesas || 0, color: '#DC2626', displayValue: stats?.despesas || 0 },
    { name: 'Saldo', value: Math.abs(saldoValue), color: saldoValue >= 0 ? '#16A34A' : '#F59E0B', displayValue: saldoValue }
  ].filter(d => d.value > 0);

  const totalFinanceiro = (stats?.receitas || 0) + (stats?.despesas || 0);

  // Monthly breakdown for line chart
  const monthlyLineData = (stats?.monthly_breakdown || []).map(m => ({
    name: m.month_name,
    saldo: m.saldo
  }));

  return (
    <div className="min-h-screen bg-[#F0F5FA] dark:bg-[#070D1A] transition-colors">
      <Header />
      <main className="transition-all duration-300">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-8 gap-4">
          <div>
            <h1 className="text-4xl font-black text-blue-900 mb-2">Dashboard</h1>
            <p className="text-slate-700">Olá, {user?.name}! Aqui está o resumo das suas finanças.</p>
          </div>
        </div>

        {/* Urgent Goals Alert */}
        {stats?.urgent_goals && stats.urgent_goals.length > 0 && (
          <Alert data-testid="urgent-goals-alert" className="mb-6 border-yellow-600 bg-yellow-50">
            <FiAlertCircle className="h-5 w-5 text-yellow-600" />
            <AlertTitle className="text-yellow-900 font-bold">Atenção: Metas Próximas do Prazo!</AlertTitle>
            <AlertDescription className="text-yellow-800">
              <div className="mt-2 space-y-2">
                {stats.urgent_goals.map((goal) => (
                  <div key={goal.goal_id} className="flex items-center justify-between">
                    <span className="font-medium">{goal.title}</span>
                    <span className="text-sm">
                      {goal.days_remaining === 0 ? 'Vence hoje!' : `${goal.days_remaining} ${goal.days_remaining === 1 ? 'dia restante' : 'dias restantes'}`} - {goal.progress.toFixed(0)}% completo
                    </span>
                  </div>
                ))}
              </div>
            </AlertDescription>
          </Alert>
        )}

        {/* Filtros */}
        <div className="bg-slate-50 border border-blue-900/10 rounded-lg p-4 mb-6">
          <div className="flex items-center gap-2 mb-4">
            <FiFilter className="text-blue-900" size={20} />
            <Label className="text-blue-900 font-semibold">Filtrar por Período</Label>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label className="text-sm text-slate-700">Ano</Label>
              <Select value={filterYear} onValueChange={setFilterYear}>
                <SelectTrigger data-testid="filter-year" className="mt-1">
                  <SelectValue placeholder="Todos os anos" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="2024">2024</SelectItem>
                  <SelectItem value="2025">2025</SelectItem>
                  <SelectItem value="2026">2026</SelectItem>
                  <SelectItem value="2027">2027</SelectItem>
                </SelectContent>
              </Select>
              {filterYear && (
                <button
                  onClick={() => {
                    setFilterYear('');
                    setFilterStartMonth('');
                    setFilterEndMonth('');
                  }}
                  className="text-xs text-blue-900 hover:underline mt-1"
                >
                  Limpar filtro
                </button>
              )}
            </div>
            
            <div>
              <Label className="text-sm text-slate-700">Mês Inicial</Label>
              <Select 
                value={filterStartMonth} 
                onValueChange={setFilterStartMonth}
                disabled={!filterYear}
              >
                <SelectTrigger data-testid="filter-start-month" className="mt-1">
                  <SelectValue placeholder={filterYear ? "Janeiro" : "Selecione ano"} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">Janeiro</SelectItem>
                  <SelectItem value="2">Fevereiro</SelectItem>
                  <SelectItem value="3">Março</SelectItem>
                  <SelectItem value="4">Abril</SelectItem>
                  <SelectItem value="5">Maio</SelectItem>
                  <SelectItem value="6">Junho</SelectItem>
                  <SelectItem value="7">Julho</SelectItem>
                  <SelectItem value="8">Agosto</SelectItem>
                  <SelectItem value="9">Setembro</SelectItem>
                  <SelectItem value="10">Outubro</SelectItem>
                  <SelectItem value="11">Novembro</SelectItem>
                  <SelectItem value="12">Dezembro</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label className="text-sm text-slate-700">Mês Final</Label>
              <Select 
                value={filterEndMonth} 
                onValueChange={setFilterEndMonth}
                disabled={!filterYear || !filterStartMonth}
              >
                <SelectTrigger data-testid="filter-end-month" className="mt-1">
                  <SelectValue placeholder={filterStartMonth ? "Dezembro" : "Selecione mês inicial"} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">Janeiro</SelectItem>
                  <SelectItem value="2">Fevereiro</SelectItem>
                  <SelectItem value="3">Março</SelectItem>
                  <SelectItem value="4">Abril</SelectItem>
                  <SelectItem value="5">Maio</SelectItem>
                  <SelectItem value="6">Junho</SelectItem>
                  <SelectItem value="7">Julho</SelectItem>
                  <SelectItem value="8">Agosto</SelectItem>
                  <SelectItem value="9">Setembro</SelectItem>
                  <SelectItem value="10">Outubro</SelectItem>
                  <SelectItem value="11">Novembro</SelectItem>
                  <SelectItem value="12">Dezembro</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          
          {filterYear && filterStartMonth && filterEndMonth && (
            <div className="mt-3">
              <p className="text-sm text-blue-900 font-medium">
                Mostrando dados de {getMonthName(parseInt(filterStartMonth))} a {getMonthName(parseInt(filterEndMonth))} de {filterYear}
              </p>
            </div>
          )}
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <Card data-testid="receitas-card" className="bg-white border border-blue-900/10 shadow-sm">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-bold uppercase tracking-wider text-blue-900/70">
                Receitas
              </CardTitle>
              <FiTrendingUp className="text-blue-900" size={20} />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-black text-blue-900">
                {formatBRL(stats?.receitas)}
              </div>
            </CardContent>
          </Card>

          <Card data-testid="despesas-card" className="bg-white border border-red-600/10 shadow-sm">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-bold uppercase tracking-wider text-red-600/70">
                Despesas
              </CardTitle>
              <FiDollarSign className="text-red-600" size={20} />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-black text-red-600">
                {formatBRL(stats?.despesas)}
              </div>
            </CardContent>
          </Card>

          <Card data-testid="saldo-card" className="bg-white border border-green-600/10 shadow-sm">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-bold uppercase tracking-wider text-green-600/70">
                Saldo
              </CardTitle>
              <FiCheck className="text-green-600" size={20} />
            </CardHeader>
            <CardContent>
              <div className={`text-3xl font-black ${
                (stats?.total_amount || 0) >= 0 ? 'text-green-600' : 'text-red-600'
              }`}>
                {formatBRL(stats?.total_amount)}
              </div>
            </CardContent>
          </Card>

          <Card data-testid="goals-card" className="bg-white border border-blue-900/10 shadow-sm">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-bold uppercase tracking-wider text-blue-900/70">
                Metas
              </CardTitle>
              <FiTarget className="text-blue-900" size={20} />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-black text-blue-900">
                {stats?.goals_percentage?.toFixed(0) || 0}%
              </div>
              <p className="text-sm text-slate-600 mt-1">
                {formatBRL(stats?.goals_progress)} de {formatBRL(stats?.goals_target)}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Donut Chart - Financeiro */}
          <Card data-testid="financeiro-chart" className="bg-white border border-blue-900/10 shadow-sm">
            <CardHeader>
              <CardTitle className="text-xl font-semibold text-blue-900">Financeiro</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="relative">
                <ResponsiveContainer width="100%" height={280}>
                  <PieChart>
                    <Pie
                      data={paymentData}
                      cx="50%"
                      cy="45%"
                      innerRadius={75}
                      outerRadius={110}
                      paddingAngle={3}
                      dataKey="value"
                      stroke="none"
                    >
                      {paymentData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip
                      formatter={(value) => [formatBRL(value), '']}
                      contentStyle={{ backgroundColor: '#FFFFFF', border: '1px solid #E2E8F0', borderRadius: '8px' }}
                    />
                  </PieChart>
                </ResponsiveContainer>
                {/* Center text */}
                <div className="absolute inset-0 flex flex-col items-center justify-center" style={{ pointerEvents: 'none', top: '-20px' }}>
                  <p className="text-sm text-slate-600">Total no período</p>
                  <p className={`text-2xl font-black ${(stats?.total_amount || 0) >= 0 ? 'text-blue-900' : 'text-red-600'}`}>
                    {formatBRL(stats?.total_amount)}
                  </p>
                </div>
              </div>
              {/* Legend */}
              <div className="flex justify-center gap-8 mt-2">
                {paymentData.map((entry) => (
                  <div key={entry.name} className="text-center">
                    <div className="flex items-center gap-2 mb-1">
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: entry.color }}></div>
                      <span className="text-xs text-slate-600">{entry.name}</span>
                    </div>
                    <p className="text-sm font-bold text-slate-800">
                      {formatBRL(entry.displayValue)}
                    </p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Area Chart - Saldo Mensal */}
          <Card data-testid="saldo-mensal-chart" className="bg-white border border-blue-900/10 shadow-sm">
            <CardHeader>
              <CardTitle className="text-xl font-semibold text-blue-900">Saldo Mensal</CardTitle>
            </CardHeader>
            <CardContent>
              {monthlyLineData.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <AreaChart data={monthlyLineData} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                    <defs>
                      <linearGradient id="saldoAreaGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#2563EB" stopOpacity={0.65} />
                        <stop offset="50%" stopColor="#3B82F6" stopOpacity={0.40} />
                        <stop offset="100%" stopColor="#93C5FD" stopOpacity={0.20} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                    <XAxis dataKey="name" stroke="#64748B" fontSize={12} tickLine={false} axisLine={{ stroke: '#E2E8F0' }} />
                    <YAxis stroke="#64748B" fontSize={12} tickLine={false} axisLine={false} />
                    <Tooltip
                      content={({ active, payload, label }) => {
                        if (active && payload && payload.length) {
                          const val = payload[0].value;
                          return (
                            <div className="bg-white/95 border border-slate-200 rounded-xl p-3 shadow-lg backdrop-blur-sm">
                              <p className="text-xs font-semibold text-slate-500 mb-1">{label}</p>
                              <p className={`text-base font-black ${val >= 0 ? 'text-blue-900' : 'text-red-600'}`}>
                                {formatBRL(val)}
                              </p>
                            </div>
                          );
                        }
                        return null;
                      }}
                    />
                    <Area
                      type="monotone"
                      dataKey="saldo"
                      stroke="#1D4ED8"
                      strokeWidth={1.5}
                      fill="url(#saldoAreaGradient)"
                      baseValue="dataMin"
                      dot={false}
                      activeDot={{
                        r: 5,
                        fill: '#1D4ED8',
                        stroke: '#FFFFFF',
                        strokeWidth: 2
                      }}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex items-center justify-center h-[300px] text-slate-500">
                  <p>Selecione um período para ver o saldo mensal</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Breakdown Charts - Despesas por Banco e Categoria */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
          {/* Bar Chart - Bancos */}
          <Card data-testid="banks-chart" className="bg-white border border-blue-900/10 shadow-sm">
            <CardHeader>
              <CardTitle className="text-xl font-semibold text-blue-900">Despesas por Banco</CardTitle>
            </CardHeader>
            <CardContent>
              {breakdowns.by_bank.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={breakdowns.by_bank} margin={{ top: 5, right: 10, left: 0, bottom: 40 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                    <XAxis dataKey="name" stroke="#475569" fontSize={11} angle={-25} textAnchor="end" interval={0} height={50} />
                    <YAxis stroke="#475569" fontSize={12} />
                    <Tooltip
                      formatter={(value) => [formatBRL(value), 'Total']}
                      contentStyle={{ backgroundColor: '#FFFFFF', border: '1px solid #E2E8F0', borderRadius: '8px' }}
                    />
                    <Bar dataKey="value" radius={[6, 6, 0, 0]}>
                      {breakdowns.by_bank.map((entry, index) => (
                        <Cell key={`bank-bar-${index}`} fill={entry.color || '#3B82F6'} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex items-center justify-center h-[300px] text-slate-500">
                  <p className="text-sm">Sem despesas no período</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Pie Chart - Categorias */}
          <Card data-testid="categories-chart" className="bg-white border border-blue-900/10 shadow-sm">
            <CardHeader>
              <CardTitle className="text-xl font-semibold text-blue-900">Despesas por Categoria</CardTitle>
            </CardHeader>
            <CardContent>
              {breakdowns.by_category.length > 0 ? (
                <ResponsiveContainer width="100%" height={340}>
                  <PieChart>
                    <Pie
                      data={breakdowns.by_category}
                      cx="50%"
                      cy="50%"
                      outerRadius={120}
                      labelLine={false}
                      label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                      dataKey="value"
                    >
                      {breakdowns.by_category.map((entry, index) => (
                        <Cell key={`cat-${index}`} fill={entry.color || CATEGORY_COLORS[index % CATEGORY_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip
                      formatter={(value) => [formatBRL(value), 'Total']}
                      contentStyle={{ backgroundColor: '#FFFFFF', border: '1px solid #E2E8F0', borderRadius: '8px' }}
                    />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex items-center justify-center h-[300px] text-slate-500">
                  <p className="text-sm">Sem despesas no período</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Bar Chart - Formas de Pagamento (last, full width) */}
        <div className="grid grid-cols-1 gap-6 mt-6">
          <Card data-testid="payment-methods-chart" className="bg-white border border-blue-900/10 shadow-sm">
            <CardHeader>
              <CardTitle className="text-xl font-semibold text-blue-900">Despesas por Forma de Pagamento</CardTitle>
            </CardHeader>
            <CardContent>
              {breakdowns.by_payment_method.length > 0 ? (
                <ResponsiveContainer width="100%" height={320}>
                  <BarChart data={breakdowns.by_payment_method} margin={{ top: 5, right: 10, left: 0, bottom: 40 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                    <XAxis dataKey="name" stroke="#475569" fontSize={11} angle={-25} textAnchor="end" interval={0} height={50} />
                    <YAxis stroke="#475569" fontSize={12} />
                    <Tooltip
                      formatter={(value) => [formatBRL(value), 'Total']}
                      contentStyle={{ backgroundColor: '#FFFFFF', border: '1px solid #E2E8F0', borderRadius: '8px' }}
                    />
                    <Bar dataKey="value" radius={[6, 6, 0, 0]}>
                      {breakdowns.by_payment_method.map((entry, index) => (
                        <Cell key={`pm-bar-${index}`} fill={entry.color || '#1E3A8A'} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex items-center justify-center h-[300px] text-slate-500">
                  <p className="text-sm">Sem despesas no período</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
      </main>
    </div>
  );
};
